import {
  CARD_TYPE_IMAGE_ACCEPT_ATTRIBUTE,
  getCardTypeImageRecommendationKey,
} from 'constants/cardTypeMessageTemplates';
import { lineStrings } from 'constants/strings';
import { cn } from 'utils/cn';
import { ECardTypeTemplateId } from 'types/cardTypeMessage';
import type {
  TCardTypeImages,
} from 'types/cardTypeMessage';

const strings = lineStrings.management.cardTypeMessage;

type SlotKey = 'a' | 'b' | 'c';

type UploadProps = {
  templateId: ECardTypeTemplateId;
  imageCount: 1 | 2 | 3;
  images: TCardTypeImages;
  uploadingSlot: SlotKey | null;
  onUpload: (slot: SlotKey, file: File) => void;
};

type PreviewProps = {
  imageCount: 1 | 2 | 3;
  images: TCardTypeImages;
  className?: string;
  /** Pictures template — 1:1 image area */
  square?: boolean;
  lazyImages?: boolean;
};

function SlotBox({
  label,
  imageUrl,
  onFileSelect,
  uploading,
  disabled,
  className,
}: {
  label: string;
  imageUrl?: string;
  onFileSelect?: (file: File) => void;
  uploading?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <label
      data-testid={`card-image-slot-${label.toLowerCase()}`}
      className={cn(
        'relative flex min-h-[72px] cursor-pointer items-center justify-center overflow-hidden border border-dashed border-border bg-white',
        className,
      )}
    >
      {imageUrl ? (
        <img src={imageUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <span className="z-10 text-lg font-semibold text-muted">{label}</span>
      )}
      {onFileSelect ? (
        <input
          type="file"
          accept={CARD_TYPE_IMAGE_ACCEPT_ATTRIBUTE}
          data-testid={`card-image-input-${label.toLowerCase()}`}
          className="absolute inset-0 cursor-pointer opacity-0"
          disabled={disabled || uploading}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onFileSelect(file);
            e.target.value = '';
          }}
        />
      ) : null}
      {uploading ? (
        <span className="absolute bottom-1 left-0 right-0 text-center text-[9px] text-muted">
          …
        </span>
      ) : null}
    </label>
  );
}

export function CardTypeMessageImageUpload({
  templateId,
  imageCount,
  images,
  uploadingSlot,
  onUpload,
}: UploadProps) {
  const isUploading = uploadingSlot !== null;
  const recommendation =
    strings.imageRecommendations[
      getCardTypeImageRecommendationKey(templateId, imageCount)
    ];

  if (imageCount === 1) {
    const singleImageAspectClass =
      templateId === ECardTypeTemplateId.Products ||
      templateId === ECardTypeTemplateId.Places
        ? 'aspect-[1024/678]'
        : 'aspect-square';

    return (
      <>
        <div className="w-full max-w-full sm:max-w-[280px]">
          <SlotBox
            label="A"
            imageUrl={images.a}
            uploading={uploadingSlot === 'a'}
            disabled={isUploading}
            onFileSelect={(file) => onUpload('a', file)}
            className={cn(singleImageAspectClass, 'min-h-[140px]')}
          />
        </div>
        <p className="mt-2 text-xs text-muted">{recommendation}</p>
      </>
    );
  }

  if (imageCount === 2) {
    return (
      <>
        <div className="grid aspect-[2/1] w-full max-w-full grid-cols-2 gap-1 sm:max-w-[320px]">
          <SlotBox
            label="A"
            imageUrl={images.a}
            uploading={uploadingSlot === 'a'}
            disabled={isUploading}
            onFileSelect={(file) => onUpload('a', file)}
            className="min-h-0"
          />
          <SlotBox
            label="B"
            imageUrl={images.b}
            uploading={uploadingSlot === 'b'}
            disabled={isUploading}
            onFileSelect={(file) => onUpload('b', file)}
            className="min-h-0"
          />
        </div>
        <p className="mt-2 text-xs text-muted">{recommendation}</p>
      </>
    );
  }

  return (
    <>
      <div className="grid aspect-[2/1] w-full max-w-full grid-cols-2 grid-rows-2 gap-1 sm:max-w-[320px]">
        <SlotBox
          label="A"
          imageUrl={images.a}
          uploading={uploadingSlot === 'a'}
          disabled={isUploading}
          onFileSelect={(file) => onUpload('a', file)}
          className="row-span-2 min-h-0"
        />
        <SlotBox
          label="B"
          imageUrl={images.b}
          uploading={uploadingSlot === 'b'}
          disabled={isUploading}
          onFileSelect={(file) => onUpload('b', file)}
          className="min-h-0"
        />
        <SlotBox
          label="C"
          imageUrl={images.c}
          uploading={uploadingSlot === 'c'}
          disabled={isUploading}
          onFileSelect={(file) => onUpload('c', file)}
          className="min-h-0"
        />
      </div>
      <p className="mt-2 text-xs text-muted">{recommendation}</p>
    </>
  );
}

export function CardTypeMessageImageGridPreview({
  imageCount,
  images,
  className,
  square = false,
  lazyImages = false,
}: PreviewProps) {
  const placeholder = (label: string) => (
    <div className="flex h-full w-full items-center justify-center bg-teal-700/90 text-2xl font-light text-white/90">
      {label}
    </div>
  );

  const cell = (url: string | undefined, label: string) =>
    url ? (
      <img
        src={url}
        alt=""
        data-testid="card-preview-image"
        className="h-full w-full object-cover"
        loading={lazyImages ? 'lazy' : undefined}
        decoding={lazyImages ? 'async' : undefined}
      />
    ) : (
      placeholder(label)
    );

  if (imageCount === 1) {
    return (
      <div
        data-testid="image-grid-preview"
        className={cn(
          'w-full overflow-hidden bg-neutral-200',
          square ? 'aspect-square' : 'aspect-[1024/678]',
          className,
        )}
      >
        {cell(images.a, 'A')}
      </div>
    );
  }

  if (imageCount === 2) {
    return (
      <div
        data-testid="image-grid-preview"
        className={cn(
          'grid aspect-[2/1] w-full grid-cols-2 gap-px overflow-hidden bg-neutral-200',
          className,
        )}
      >
        <div className="overflow-hidden">{cell(images.a, 'A')}</div>
        <div className="overflow-hidden">{cell(images.b, 'B')}</div>
      </div>
    );
  }

  return (
    <div
      data-testid="image-grid-preview"
      className={cn(
        'grid aspect-[2/1] w-full grid-cols-2 grid-rows-2 gap-px overflow-hidden bg-neutral-200',
        className,
      )}
    >
      <div className="row-span-2 overflow-hidden">{cell(images.a, 'A')}</div>
      <div className="overflow-hidden">{cell(images.b, 'B')}</div>
      <div className="overflow-hidden">{cell(images.c, 'C')}</div>
    </div>
  );
}
